<template>
  <component
    :is="component"
    v-b-tooltip.hover.focus.top="{
      delay: { show: 500, hide: 0 },
      title: tooltipText
    }"
    :title="$filters.capitalize(variant)"
    class="ld-token"
    :class="[
      {
        'ld-token--draggable': draggable,
        'ld-token-disabled': disabled,
        'ld-token--actionable': isActionable,
        'ld-token--outline': hasOutline
      },
      variant ? `ld-token--${variant}` : ''
    ]"
  >
    <div
      v-if="draggable"
      class="ld-token__handle"
    />

    <div
      v-if="hasPrepend"
      class="ld-token__slot-container"
    >
      <slot name="prepend" />
    </div>

    <span
      class="align-middle"
      data-testid="ld-token"
    >
      {{ label }}
    </span>
  </component>
</template>

<script>
import { VBTooltip } from '@ui/components'

const VALID_VARIANTS = [
  'arr',
  'obj',
  'string',
  'number',
  'invalid'
]

export default {
  directives: {
    'b-tooltip': VBTooltip
  },
  props: {
    // Root component (default is span)
    component: {
      type: String,
      default: 'span'
    },
    // Text label
    label: {
      type: String,
      default: ''
    },
    // Show draggable handle (indicator only)
    draggable: {
      type: Boolean,
      default: false
    },
    // show cursor as pointer and hover/focus states
    isActionable: {
      type: Boolean,
      default: false
    },
    // Set color variant
    variant: {
      type: String,
      default: '',
      validator(val) {
        return !val || VALID_VARIANTS.includes(val)
      }
    },
    hasOutline: {
      type: Boolean
    },
    tooltipText: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    hasPrepend() {
      return !!this.$slots.prepend
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~@ui/styles/src/scss/utils/lib";

$variant-color-map: (
  --arr: #c9c,
  --obj: #fc6,
  --string: #9c9,
  --number:#f99157,
  --invalid: #b8babd
);
$horizontal-spacing: 6px;

.ld-token {
  $this: &;

  display: inline-flex;
  align-items: center;
  margin: 0.1rem;
  padding: 4px $horizontal-spacing;
  font-size: $tinyFont;
  font-family: $monospaceFontStack;
  vertical-align: middle;
  word-break: break-word;
  background-color: scale-color($neutralGray, $lightness: 95%);
  border: 1px solid #000;
  border-radius: 2px;
  cursor: default;

  &.sortable-ghost {
    opacity: 0.4;
  }

  &--actionable:not(.ld-token-disabled) {
    cursor: pointer;
  }

  &--draggable {
    cursor: move;
    cursor: grab;

    #{$this}__handle {
      display: inline-block;
    }
  }

  // Variant background colors
  @each $variant, $color in $variant-color-map {
    &#{$variant} {
      background-color: $color;

      &#{$this}--actionable:not(.ld-token-disabled) {
        &:hover {
          background-color: darken($color, 15%);
        }
      }
    }
  }

  &--invalid {
    padding: 12px 8.7px;
  }

  &--outline {
    border-color: $primaryColor;
  }

  &__slot-container {
    display: flex;
    margin-right: $horizontal-spacing;
  }

  &__handle {
    display: none;
    width: 6px;
    height: 11px;
    margin-right: 3px;
    vertical-align: middle;
    background-image: $dragHandleTransparentImage;
  }
}
</style>
